import { ProStudioContext } from '../xstate/prostudio.xstate.types';

type SolutionsList = ProStudioContext['solutions'];

/**
 * PSTUDIO-3671. Decide whether the solution referenced by the current URL is
 * genuinely gone (deleted) vs. simply not loaded yet / still provisioning.
 *
 * Disambiguation (AC2): a freshly-created solution DOES appear in the loaded
 * `solutions` list (loadBots returns it) before its version row materializes;
 * a deleted solution is absent. So we only ever return `true` once the list is
 * a loaded array AND the URL target has no match. While the list is undefined
 * (still loading) we never eject the user.
 */
export function isSolutionNotFound(args: {
  solutions: SolutionsList;
  botName?: string | null;
  botIdURLParam?: string | null;
}): boolean {
  const { solutions, botName, botIdURLParam } = args;

  if (!Array.isArray(solutions)) return false;

  const name = botName?.trim();
  if (name) {
    return !solutions.some((s) => s?.name === name);
  }

  const id = botIdURLParam?.trim();
  if (id) {
    return !solutions.some((s) => s?.id === id);
  }

  // No target solution in the URL => this is the All Solutions view.
  return false;
}

/**
 * PSTUDIO-3671. Decide whether an always-on `solutionVersionSubscription` push
 * proves the OPEN version was deleted — the backend-anchored, cross-machine
 * detection the ticket's fix #1 calls for (a same-browser BroadcastChannel and
 * a coworker-focus-gated subscription do NOT cover a user who merely has the
 * fully-loaded editor open on another machine).
 *
 * Disambiguation (fix #2 — deleted vs. not-yet-materialized): a definitive null
 * for a `by_pk` version is ambiguous on its own — a freshly-provisioning
 * solution also yields transient nulls before its version row exists. We only
 * treat a null as a deletion once the subscription has ALREADY delivered a
 * non-null payload for this same version (`hasSeenVersionData`): data was there,
 * now it is gone. A null that was never preceded by data is provisioning — never
 * eject. The caller arms `hasSeenVersionData` on the first non-null push and
 * resets it whenever it re-subscribes to a different version.
 */
export function shouldEjectOnDeletedVersion(args: {
  hasSeenVersionData: boolean;
  versionData: unknown;
}): boolean {
  return args.hasSeenVersionData && args.versionData == null;
}

/**
 * PSTUDIO-3671. Optimistically drop the just-ejected solution from the loaded
 * `solutions` list.
 *
 * On eject the machine parks in `#solutionNotFound` and `Root` redirects to All
 * Solutions, which renders from `ctx.solutions`. That list was loaded while the
 * (now deleted) solution still existed, so without this it would keep showing
 * the deleted row until a full refetch — the deleting tab avoids that only
 * because it re-runs `loadBots`. Every eject trigger (backend-subscription null,
 * cross-tab broadcast, reload not-found, coworker-focus null) means the OPEN
 * solution is the one that is gone, so we can safely remove exactly it, matching
 * by the same keys `isSolutionNotFound` uses (name first, then id). Read the
 * open target from the PRE-clear context.
 */
export function removeSolutionFromList(args: {
  solutions: SolutionsList;
  botName?: string | null;
  botIdURLParam?: string | null;
  botId?: string | null;
}): SolutionsList {
  const { solutions, botName, botIdURLParam, botId } = args;

  if (!Array.isArray(solutions)) return solutions;

  const name = botName?.trim();
  if (name) return solutions.filter((s) => s?.name !== name);

  const id = (botIdURLParam ?? botId)?.trim();
  if (id) return solutions.filter((s) => s?.id !== id);

  return solutions;
}
